/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Sidebar } from "../../../shared";
import { Menu } from "primereact/menu";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  Boton,
  CampoInformacion,
  Formulario,
  accessAPI,
  downloadFile,
  exportarExcel,
  TablaFiltrable,
  Titulo
} from "../../../shared";

export default function TarjetaProductos({ proceso, productos, setProductos }) {
  const [columnas, setColumnas] = useState([]);
  const [expandedRows, setExpandedRows] = useState(null);
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [descargandoEtiquetaId, setDescargandoEtiquetaId] = useState(null);

  const toast = useRef(null);
  const menuRef = useRef(null);

  // Arma las columnas de la tabla basándose en los campos identificadores
  useEffect(() => {
    const cols = [];
    for (const campo of proceso.certificadotipo.productocampospropios) {
      if (campo.identificadorursea) {
        cols.push({
          field: campo.id.toString(),
          header: campo.nombre
        });
      }
    }
    setColumnas(cols);
  }, [proceso.certificadotipo.productocampospropios]);

  // Prepara los datos para la tabla
  const productosParaTabla = productos.map((producto) => {
    const row = { ...producto, _original: producto };
    for (const campo of producto.producto_x_productocampospropios || []) {
      const campoId =
        campo.productocampospropiosid || campo.productocampospropios?.id;
      if (campoId) {
        row[campoId.toString()] = campo.valor;
      }
    }
    return row;
  });

  // La tabla sólo muestra los campos identificadores; el resto se ve al
  // expandir cada producto. El Excel lleva todo: todos los campos propios del
  // tipo de certificado, las características críticas y el estado.
  const exportarProductos = ({ data, nombreArchivo }) => {
    // La etiqueta y las unidades salen de los campos de cada producto, igual
    // que en la fila expandida.
    const definiciones = new Map();
    for (const row of data) {
      for (const campo of row._original.producto_x_productocampospropios || []) {
        const campoId =
          campo.productocampospropiosid || campo.productocampospropios?.id;
        if (campoId && !definiciones.has(campoId)) {
          definiciones.set(campoId, campo.productocampospropios);
        }
      }
    }
    const camposPropios = [...definiciones].map(([campoId, definicion]) => ({
      header: definicion?.etiqueta || campoId.toString(),
      value: (row) => {
        const valor = row[campoId.toString()];
        if (!valor) return "";
        return definicion?.unidades ? `${valor} ${definicion.unidades}` : valor;
      }
    }));

    const etiquetasCriticas = [
      ...new Set(
        data.flatMap((row) =>
          (row._original.producto_x_productocaracteristicascriticas || [])
            .map((caract) => caract.caracteristicacritica?.etiqueta)
            .filter(Boolean)
        )
      )
    ];
    const caracteristicasCriticas = etiquetasCriticas.map((etiqueta) => ({
      header: etiqueta,
      value: (row) =>
        (row._original.producto_x_productocaracteristicascriticas || []).find(
          (caract) => caract.caracteristicacritica?.etiqueta === etiqueta
        )?.valor || ""
    }));

    const estado = {
      header: "Estado",
      value: (row) => {
        const producto = row._original;
        if (producto.vigente === 0) return "No vigente";
        if (!producto.altaemisioncertificado?.id) return "No emitido";
        if (producto.cambioproductocertificado?.length > 0) {
          return "Con cambios pendientes de emisión";
        }
        return "Emitido";
      }
    };

    exportarExcel({
      data,
      columnas: [...camposPropios, ...caracteristicasCriticas, estado],
      nombreArchivo
    });
  };

  // Template para indicador de modificaciones
  const modificacionTemplate = (rowData) => {
    const producto = rowData._original;
    const noEmitido = !producto.altaemisioncertificado?.id;
    const tieneModificaciones = producto.cambioproductocertificado?.length > 0;
    const noVigente = producto.vigente === 0;

    if (noVigente || tieneModificaciones || noEmitido) {
      return (
        <Tag
          severity="danger"
          value={<i className="pi pi-exclamation-triangle"></i>}
        />
      );
    }
    return null;
  };

  // Función para descargar etiqueta
  function descargarEtiqueta(producto) {
    if (producto.etiqueta?.length > 0) {
      const etiqueta = producto.etiqueta[0];
      const nombreArchivo =
        etiqueta.archivo?.nombredescarga || "etiqueta.pdf";

      setDescargandoEtiquetaId(producto.id);
      downloadFile(
        `admin/certificado/descargaretiqueta/${etiqueta.id}`,
        nombreArchivo,
        () => setDescargandoEtiquetaId(null),
        (error) => {
          setDescargandoEtiquetaId(null);
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: error[0]?.msg || "No se pudo descargar la etiqueta",
            sticky: true
          });
        }
      );
    }
  }

  // Genera los items del menú según el producto seleccionado
  const getMenuItems = () => {
    if (!productoSeleccionado) return [];

    const producto = productoSeleccionado._original;
    const cambiosPendientes = producto.cambioproductocertificado || [];
    const marcadoParaEliminar = cambiosPendientes.some((c) => c.bajaproducto);
    const tieneEtiqueta = producto.etiqueta?.length > 0;

    const items = [];

    // Opción de descargar etiqueta
    if (tieneEtiqueta) {
      items.push({
        label: "Descargar etiqueta",
        icon: "pi pi-download",
        command: () => descargarEtiqueta(producto)
      });
    }

    // Opción de editar (siempre disponible si el producto está vigente)
    if (producto.vigente !== 0) {
      items.push({
        label: "Editar",
        icon: "pi pi-pencil",
        command: () => setSidebarEditar(true)
      });
    }

    // Opción de eliminar del certificado
    if (!marcadoParaEliminar && producto.vigente !== 0) {
      items.push({
        label: "Eliminar",
        icon: "pi pi-trash",
        command: () => {
          confirmDialog({
            message:
              "¿Está seguro que desea eliminar este producto del certificado?",
            header: "Confirmar eliminación",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sí",
            rejectLabel: "No",
            accept: () => marcarParaEliminar(producto.id)
          });
        }
      });
    }

    return items;
  };

  // Template para acciones en la tabla
  const accionesTemplate = (rowData) => {
    const producto = rowData._original;

    // No mostrar menú si el producto no está vigente
    if (producto.vigente === 0) {
      return null;
    }

    const descargando = descargandoEtiquetaId === producto.id;

    return (
      <div css={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5em" }}>
        {descargando && <i className="pi pi-spin pi-spinner" />}
        <Boton
          icono
          negro
          callback={(e) => {
            setProductoSeleccionado(rowData);
            menuRef.current.toggle(e);
          }}
        >
          <i className="pi pi-ellipsis-v" />
        </Boton>
      </div>
    );
  };

  // Elimina un cambio pendiente
  function eliminarCambio(productoId, cambioId) {
    accessAPI(
      "DELETE",
      `admin/producto/cambioproductocertificado/${cambioId}`,
      null,
      () => {
        setProductos((prevProductos) =>
          prevProductos.map((p) => {
            if (p.id !== productoId) return p;
            return {
              ...p,
              cambioproductocertificado: (
                p.cambioproductocertificado || []
              ).filter((c) => c.id !== cambioId)
            };
          })
        );
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Cambio eliminado"
        });
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al eliminar cambio",
          sticky: true
        });
      }
    );
  }

  // Marca un producto para eliminar
  function marcarParaEliminar(productoId) {
    accessAPI(
      "PUT",
      `admin/producto/marcareliminar/${productoId}`,
      null,
      (respuesta) => {
        if (respuesta[0]?.eliminar) {
          setProductos((prev) => prev.filter((p) => p.id !== productoId));
          toast.current?.show({
            severity: "success",
            summary: "Éxito",
            detail: "Producto eliminado del proceso"
          });
        } else {
          setProductos((prevProductos) =>
            prevProductos.map((p) => {
              if (p.id !== productoId) return p;
              return {
                ...p,
                cambioproductocertificado: [
                  ...(p.cambioproductocertificado || []),
                  respuesta
                ]
              };
            })
          );
          toast.current?.show({
            severity: "success",
            summary: "Éxito",
            detail: "Producto marcado para eliminar en próxima emisión"
          });
        }
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al eliminar producto",
          sticky: true
        });
      }
    );
  }

  // Función para editar un producto
  function handleEditarProducto(contenidoCampos) {
    if (!productoSeleccionado) return;

    setLoaderEditar(true);
    const producto = productoSeleccionado._original;

    let camposPropios = {};
    let caracteristicasCriticas = {};

    // Itera el objeto buscando campos propios y características críticas
    // Todos los valores se parsean a string
    for (const key in contenidoCampos) {
      const valor = contenidoCampos[key];
      const valorString = valor != null ? String(valor) : "";

      if (key.startsWith("cc_")) {
        const caracteristicaId = key.replace("cc_", "");
        caracteristicasCriticas[caracteristicaId] = valorString;
      } else {
        camposPropios[key] = valorString;
      }
    }

    const camposParaMandar = {
      campospropios: camposPropios,
      caracteristicascriticas: caracteristicasCriticas
    };

    accessAPI(
      "PUT",
      `admin/producto/${producto.id}`,
      camposParaMandar,
      (respuesta) => {
        setLoaderEditar(false);
        setSidebarEditar(false);
        // Actualizar el producto en la lista
        setProductos((prevProductos) =>
          prevProductos.map((p) => {
            if (p.id !== producto.id) return p;
            return respuesta.productoActualizado;
          })
        );
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta.msg || "Producto actualizado correctamente"
        });
      },
      (error) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al actualizar producto",
          sticky: true
        });
      }
    );
  }

  // Genera la configuración del formulario de editar producto
  const generarFormConfigEditar = () => {
    if (!productoSeleccionado) return null;

    const producto = productoSeleccionado._original;

    const config = {
      secciones: [],
      botones: [
        {
          etiqueta: "Guardar cambios",
          callback: handleEditarProducto,
          validar: true
        }
      ]
    };

    // Agregar sección de campos propios del producto
    if (proceso.certificadotipo?.productocampospropios?.length > 0) {
      const seccionCamposPropios = {
        nombre: "Campos del producto",
        campos: []
      };

      for (const campoPropio of proceso.certificadotipo.productocampospropios) {
        // Buscar el valor actual del campo en el producto
        const campoActual = producto.producto_x_productocampospropios?.find(
          (c) =>
            c.productocampospropiosid === campoPropio.id ||
            c.productocampospropios?.id === campoPropio.id
        );

        const campoParaConfigurar = {
          nombre: campoPropio.id.toString(),
          etiqueta: campoPropio.unidades
            ? `${campoPropio.etiqueta} (${campoPropio.unidades})`
            : campoPropio.etiqueta,
          notas: campoPropio.notas,
          requerido: campoPropio.obligatoriobool ? true : false,
          respuesta: campoActual?.valor || ""
        };

        // Determinar tipo de campo
        switch (campoPropio.tipo) {
          case "int":
            campoParaConfigurar.tipoitemformularioid = 4;
            break;
          case "float":
            campoParaConfigurar.tipoitemformularioid = 8;
            break;
          default:
            campoParaConfigurar.tipoitemformularioid = 1;
            break;
        }

        // Si tiene opciones, convertir a dropdown
        if (campoPropio.opcionesdropdown?.length) {
          campoParaConfigurar.opciones = campoPropio.opcionesdropdown;
          campoParaConfigurar.sinOpcionInicial = true;
          campoParaConfigurar.tipoitemformularioid = 5;
        }

        // Si tiene script de autocompletar, deshabilitar
        if (campoPropio.autocompletarScript) {
          campoParaConfigurar.disabled = true;
          // Texto, aunque el campo sea numérico: el cartel es un string y un
          // InputNumber lo muestra como NaN. El tipo guardado ya se fuerza a
          // string al cargar el script, pero se puede volver a cambiar desde
          // la edición del campo, así que acá no se depende de eso.
          campoParaConfigurar.tipoitemformularioid = 1;
          campoParaConfigurar.respuesta = "Autocompletado por script";
        }

        seccionCamposPropios.campos.push(campoParaConfigurar);
      }

      config.secciones.push(seccionCamposPropios);
    }

    // Agregar sección de características críticas
    if (proceso.certificadotipo?.ProductoCaracteristicasCriticas?.length > 0) {
      const seccionCaracteristicasCriticas = {
        nombre: "Características críticas",
        campos: []
      };

      for (const caracteristica of proceso.certificadotipo
        .ProductoCaracteristicasCriticas) {
        // Buscar el valor actual de la característica en el producto
        const caractActual =
          producto.producto_x_productocaracteristicascriticas?.find(
            (c) => c.caracteristicacritica?.id === caracteristica.id
          );

        seccionCaracteristicasCriticas.campos.push({
          nombre: `cc_${caracteristica.id}`,
          etiqueta: caracteristica.etiqueta,
          notas: caracteristica.notas,
          tipoitemformularioid: 1,
          requerido: false,
          respuesta: caractActual?.valor || ""
        });
      }

      config.secciones.push(seccionCaracteristicasCriticas);
    }

    return config;
  };

  // Template para fila expandida
  const rowExpansionTemplate = (data) => {
    const producto = data._original;
    const camposPropios = producto.producto_x_productocampospropios || [];
    const caracteristicasCriticas =
      producto.producto_x_productocaracteristicascriticas || [];
    const cambiosPendientes = producto.cambioproductocertificado || [];

    return (
      <div
        css={{
          padding: "1rem",
          backgroundColor: "var(--surface-ground)",
          borderRadius: "6px"
        }}
      >
        {producto.vigente === 0 ? (
          <p css={{ color: "var(--red-500)", fontWeight: "bold" }}>
            Producto no vigente
          </p>
        ) : (
          <>
            {/* Estado de emisión */}
            {!producto.altaemisioncertificado?.id && (
              <div css={{ marginBottom: "1rem" }}>
                <Tag severity="warning" value="Producto no emitido" />
              </div>
            )}

            {/* Campos propios */}
            <div className="grid">
              {camposPropios.map((campo) => (
                <CampoInformacion
                  key={campo.id}
                  nombreCampo={campo.productocampospropios?.etiqueta}
                >
                  {campo.valor
                    ? `${campo.valor}${campo.productocampospropios?.unidades ? ` ${campo.productocampospropios.unidades}` : ""}`
                    : "-"}
                </CampoInformacion>
              ))}
            </div>

            {/* Características críticas */}
            {caracteristicasCriticas.length > 0 && (
              <div css={{ marginTop: "1.5rem" }}>
                <h4
                  css={{ marginBottom: "0.5rem", color: "var(--text-color)" }}
                >
                  Características críticas
                </h4>
                <div className="grid">
                  {caracteristicasCriticas.map((caract) => (
                    <CampoInformacion
                      key={caract.id}
                      nombreCampo={caract.caracteristicacritica?.etiqueta}
                    >
                      {caract.valor || "-"}
                    </CampoInformacion>
                  ))}
                </div>
              </div>
            )}

            {/* Cambios pendientes */}
            {cambiosPendientes.length > 0 && (
              <div css={{ marginTop: "1.5rem" }}>
                <h4
                  css={{
                    marginBottom: "0.5rem",
                    color: "var(--amarillo)"
                  }}
                >
                  Cambios pendientes de emisión
                </h4>
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem"
                  }}
                >
                  {cambiosPendientes.map((cambio) => (
                    <div
                      key={cambio.id}
                      css={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0.75rem",
                        backgroundColor: "var(--amarilloLight)",
                        borderRadius: "6px",
                        borderLeft: "3px solid var(--amarillo)"
                      }}
                    >
                      <span>
                        {cambio.bajaproducto ? (
                          <strong>Producto marcado para eliminar</strong>
                        ) : (
                          <>
                            <strong>
                              {
                                cambio.producto_x_productocampospropios
                                  ?.productocampospropios?.etiqueta
                              }
                              :
                            </strong>{" "}
                            {cambio.valornuevo}
                          </>
                        )}
                      </span>
                      <Boton
                        rojo
                        texto
                        confirmacion="¿Deshacer este cambio?"
                        callback={() => eliminarCambio(producto.id, cambio.id)}
                      >
                        Deshacer
                      </Boton>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={getMenuItems()} popup ref={menuRef} />
      <Card>
        {productos.length > 0 ? (
          <TablaFiltrable conFiltros={false}
            key={`productos-${descargandoEtiquetaId}`}
            value={productosParaTabla}
            size="small"
            dataKey="id"
            expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowExpansionTemplate={rowExpansionTemplate}
            emptyMessage="No hay productos"
            nombreArchivo="productos_proceso"
            exportar={exportarProductos}
          >
            <Column expander style={{ width: "3rem" }} />
            {columnas.map((col) => (
              <Column key={col.field} field={col.field} header={col.header} />
            ))}
            <Column
              body={modificacionTemplate}
              style={{ width: "4rem", textAlign: "center" }}
            />
            <Column
              body={accionesTemplate}
              style={{ width: "4rem", textAlign: "center" }}
            />
          </TablaFiltrable>
        ) : (
          <p css={{ color: "var(--grisTextos)" }}>
            No hay productos en este proceso
          </p>
        )}
      </Card>

      {/* Sidebar Agregar Producto */}
      {/* Sidebar Editar Producto */}
      <Sidebar
        visible={sidebarEditar}
        onHide={() => setSidebarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {productoSeleccionado && (
          <>
            <Titulo>Editar producto</Titulo>
            <Formulario
              key={productoSeleccionado._original.id}
              config={generarFormConfigEditar()}
              loader={loaderEditar}
            />
          </>
        )}
      </Sidebar>
    </>
  );
}
